import React, { useState, useEffect, useCallback, useMemo } from 'react'

import _ from 'lodash'
import { Icon, Button, Row, notification, Divider, Select, Typography } from 'antd'
import { Formik } from 'formik'
import { v4 as uuidV4 } from 'uuid'

import CustomInput from '../../../components/CustomInput/customInput'
import FormHeader from '../../../components/FormHeader/FormHeader'
import { api, request } from '../../../util/handlerRequestUtil'
import { getMessage } from '../../../util/IntlMessages'
import Forecast from '../models/Forecast'
import CommercialProcessesItems from './items/CommercialProcessesItems'
import SoyAreaItems from './items/SoyAreaItems'

async function storeForecasts(data) {
  try {
    const forecast = await request(api.storeForecasts(data))
    notification.success({
      description: getMessage('successStoreForecasts'),
      message: getMessage('sweetAlerts.success')
    })

    return forecast
  } catch (error) {
    console.tron.log(error)
  }
}

async function updateForecasts(data, id) {
  try {
    await request(api.updateForecasts(id, data))
    notification.success({
      description: getMessage('successUpdateForecasts'),
      message: getMessage('sweetAlerts.success')
    })
  } catch (error) {
    console.tron.log(error)
  }
}

const CreateForecasts = function ({ customerId, districts }) {
  const [soyAreaItems, setSoyAreaItems] = useState([])
  const [commercialProcessesItems, setCommercialProcessesItems] = useState([])
  const [formValues, setFormValues] = useState({})
  const [forecastTypeReservedId, setForecastTypeReservedId] = useState(null)
  const [selectedDistrict, setSelectedDistrict] = useState(null)
  const areaIntactaLegal = useMemo(
    () =>
      soyAreaItems
        .filter((item) => item.forecastTypeId === forecastTypeReservedId)
        .reduce((acc, item) => acc + parseInt(item.area), 0),
    [soyAreaItems]
  )
  const areaTotal = useMemo(() => soyAreaItems.reduce((acc, item) => acc + parseInt(item.area), 0), [soyAreaItems])
  const commercialProcessesTotalArea = useMemo(
    () => commercialProcessesItems.reduce((acc, item) => acc + parseInt(item.area), 0),
    [commercialProcessesItems]
  )

  const handleSubmit = useCallback(
    async (values) => {
      const id = parseInt(values.id) || null
      const forecast = new Forecast({
        ...values,
        soyAreaItems: formatObjects(soyAreaItems),
        commercialProcessesItems: formatObjects(commercialProcessesItems),
        areaTotal,
        areaIntactaLegal,
        commercialProcessesTotalArea
      })

      if (!forecast.id) {
        forecast.customerId = customerId
        const data = forecast.toApi()
        const forecastCreateDto = {
          forecastDate: new Date().toISOString().split('T')[0],
          districtId: selectedDistrict,
          active: true,
          deleted: false,
          forecastCommercialProcessesItem: data.commercialProcessesItems,
          forecastItems: data.soyAreaItems
        }

        const forecastNewData = await storeForecasts(forecastCreateDto)
        setFormValues({ ...data, id: forecastNewData.id })
      } else {
        const data = forecast.toApi()
        const forecastUpdateDto = {
          customerId: data.customerId,
          forecastDate: formValues.forecastDate,
          forecastCommercialProcessesItem: data.commercialProcessesItems,
          forecastItems: data.soyAreaItems,
          districtId: selectedDistrict
        }
        updateForecasts(forecastUpdateDto, id).then(() => retrieveForecasts(customerId, selectedDistrict))
      }
    },
    [soyAreaItems, commercialProcessesItems, areaTotal, areaIntactaLegal, commercialProcessesTotalArea]
  )

  useEffect(() => {
    if (customerId && selectedDistrict) {
      retrieveForecasts(customerId, selectedDistrict)
      getForecastsType()
    } else {
      setFormValues({})
    }
  }, [selectedDistrict])

  function formatObjects(items) {
    const newValuesFormated = []
    for (const element of items) {
      delete element.id
      newValuesFormated.push(element)
    }

    return newValuesFormated
  }

  async function retrieveForecasts(customerId, districtId) {
    try {
      const res = await request(api.retrieveForecasts(customerId, { districtId }))
      setFormValues(res)
      setSoyAreaItems(res.forecastItems ? res.forecastItems : [])
      setCommercialProcessesItems(
        res.forecastCommercialProcessesItem
          ? res.forecastCommercialProcessesItem.map((item) => ({
              ...item,
              predictedCommercialProcesses: item.predictedCommercialProcesses.split(',')
            }))
          : []
      )
    } catch (error) {
      console.tron.log(error)
    }
  }

  async function getForecastsType() {
    const res = await request(api.getForecastsType({ active: true, name: 'RESERVED' }))
    setForecastTypeReservedId(res[0].value)
  }

  const addSoyAreaItem = useCallback(
    (newItem) => {
      const itemToUpdateIndex = soyAreaItems.findIndex(
        ({ tecnologiaId, forecastTypeId }) =>
          newItem.tecnologiaId === tecnologiaId && newItem.forecastTypeId === forecastTypeId
      )
      let newItems = soyAreaItems

      if (itemToUpdateIndex > -1) {
        newItems[itemToUpdateIndex] = {
          ...newItem,
          id: uuidV4()
        }
      } else {
        newItems.push({
          ...newItem,
          id: uuidV4()
        })
      }

      setSoyAreaItems(newItems)
    },
    [soyAreaItems]
  )

  const addCommercialProcessesItem = useCallback(
    (newItem) => {
      const itemToUpdateIndex = commercialProcessesItems.findIndex(
        ({ predictedCommercialProcesses }) =>
          Array.isArray(newItem.predictedCommercialProcesses) &&
          Array.isArray(predictedCommercialProcesses) &&
          _.isEqual(newItem.predictedCommercialProcesses, predictedCommercialProcesses)
      )
      let newItems = commercialProcessesItems

      if (itemToUpdateIndex > -1) {
        newItems[itemToUpdateIndex] = {
          ...newItem,
          id: uuidV4()
        }
      } else {
        newItems.push({
          ...newItem,
          id: uuidV4()
        })
      }

      setCommercialProcessesItems(newItems)
    },
    [commercialProcessesItems]
  )

  const handleDeleteSoyAreaItem = useCallback(
    async (id) => {
      try {
        await request(api.updateForecastItems(id, { last: false, active: false }))
        setSoyAreaItems(soyAreaItems.filter((item) => item.id !== id))
        notification.success({
          description: getMessage('successRemoveSoyAreaForecast'),
          message: getMessage('sweetAlerts.success')
        })
      } catch (error) {
        console.tron.log(error)
      }
    },
    [soyAreaItems]
  )

  const handleDeleteCommercialProcessesItem = useCallback(
    async (id) => {
      try {
        await request(api.updateForecastCommercialProcessesItem(id, { last: false, active: false }))
        setCommercialProcessesItems(commercialProcessesItems.filter((item) => item.id !== id))
        notification.success({
          description: getMessage('successRemoveCommercialProcesses'),
          message: getMessage('sweetAlerts.success')
        })
      } catch (error) {
        console.tron.log(error)
      }
    },
    [commercialProcessesItems]
  )

  const selectedDistrictDescription = useMemo(
    () => (districts || []).find((district) => district.value === selectedDistrict)?.description,
    [selectedDistrict]
  )

  if (!selectedDistrict && districts.length > 0) {
    if (districts.length > 1) {
      return (
        <Select
          style={{ width: 300, margin: '48px 0 48px 50%', transform: 'translate(-50%)' }}
          placeholder="Selecione um distrito"
          onChange={setSelectedDistrict}
        >
          {districts.map(({ value, description }) => (
            <Select.Option key={value} value={value}>
              <p style={{ margin: '0px', marginLeft: '5px' }}>{description}</p>
            </Select.Option>
          ))}
        </Select>
      )
    } else {
      setSelectedDistrict(districts[0].value)
    }
  }

  return (
    <div>
      <Formik
        enableReinitialize={true}
        initialValues={formValues}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ submitForm, errors, handleBlur, setFieldValue }) => {
          return (
            <div className="contacts-form">
              <Row type="flex" justify="space-between" align="center" style={{ padding: '2%' }}>
                <Typography.Title level={4} style={{ margin: 0, lineHeight: 2 }}>
                  Distrito: {selectedDistrictDescription}
                </Typography.Title>
                <Row type="flex">
                  {districts.length > 1 && (
                    <Button type="secondary" className="button-action" onClick={() => setSelectedDistrict(null)}>
                      <Icon type="left" theme="outlined" className="default-back-icon" />
                      {getMessage('label.back')}
                    </Button>
                  )}
                  <Button type="primary" className="button-action" onClick={submitForm}>
                    {formValues.id ? getMessage('update') : getMessage('register')}
                  </Button>
                </Row>
              </Row>
              <FormHeader
                color="#000"
                title={getMessage('customer.forecast.title')}
                orientation="left"
                type="horizontal"
              />
              <SoyAreaItems
                soyAreaItems={soyAreaItems}
                onAddItem={addSoyAreaItem}
                onDeleteItem={handleDeleteSoyAreaItem}
                handleBlur={handleBlur}
              />
              <Divider />
              <Row type="flex" justify="start">
                <CustomInput
                  disabled={true}
                  field="areaIntactaLegal"
                  errors={errors}
                  setFieldValue={setFieldValue}
                  rowSpan={6}
                  value={{ areaIntactaLegal }}
                  type="number"
                  defaultValue={0}
                  handleBlur={handleBlur}
                />
                <CustomInput
                  disabled={true}
                  field="areaTotal"
                  errors={errors}
                  setFieldValue={setFieldValue}
                  rowSpan={6}
                  value={{ areaTotal }}
                  type="number"
                  defaultValue={0}
                  handleBlur={handleBlur}
                />
              </Row>
              <FormHeader
                color="#000"
                title={getMessage('customer.forecast.commercial.process.predicted.tille')}
                orientation="left"
                type="horizontal"
              />
              <CommercialProcessesItems
                commercialProcessesItems={commercialProcessesItems}
                onAddItem={addCommercialProcessesItem}
                onDeleteItem={handleDeleteCommercialProcessesItem}
                handleBlur={handleBlur}
              />
              <Row type="flex" justify="start">
                <CustomInput
                  disabled={true}
                  field="commercialProcessesTotalArea"
                  errors={errors}
                  setFieldValue={setFieldValue}
                  rowSpan={6}
                  value={{ commercialProcessesTotalArea }}
                  type="number"
                  defaultValue={0}
                  handleBlur={handleBlur}
                />
              </Row>
            </div>
          )
        }}
      </Formik>
    </div>
  )
}

export default CreateForecasts
